import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Card, Table, Select, Button, Tooltip, Popover } from 'antd'
import { TableProps } from 'antd/lib/table'
import { CommonFilters, UserAvatar, TableDownload } from '@components'
import { TableColumnProps } from '@hooks'
import { needTotalEnum, CustomerMergeApprovalFilter, CustomerMergeApprovalItem } from '@types'
import { antdSortMap } from '@configs/map'
import { getCustomerMergeApprovalList, exportCustomerMergeApprovalList } from '@services/customer'
import { getSortOrder, getTableXwidth } from '@configs/table'
import { FieldItem } from '../../../components/CommonFilters/configs'
import { useTableService } from '@hooks/useTableService'
import { trimStr, formatTime } from '@library'
const initFilter: CustomerMergeApprovalFilter = {
  page: 1,
  pageSize: 10,
  needTotal: needTotalEnum.ALL,
}
const StatusMap = {
  0: '待审核',
  1: '审核成功',
  2: '审核失败',
  3: '合并成功',
  4: '合并失败',
}

const AssignExamine = () => {
  const {
    loading,
    list,
    pagination,
    filter,
    setFilter,
    resetFilter,
    rowKey,
  } = useTableService(getCustomerMergeApprovalList, initFilter, '获取客户合并审批记录失败')

  const onTableChange: TableProps<CustomerMergeApprovalItem>['onChange'] = (pagination, filters, sorter) => {
    const sort = getSortOrder<CustomerMergeApprovalFilter>(sorter)
    setFilter(sort)
  }

  const filterItems: FieldItem[] = [
    {
      fieldName: 'createTime',
      label: '申请时间',
      type: 'rangePicker',
      handleValue: val => ({
        createTimeFrom: formatTime(val?.[0], 'start'),
        createTimeTo: formatTime(val?.[1], 'end'),
      }),
    },
    { fieldName: 'mergedCustomerName', label: '被合并客户名', type: 'customerNameSelect' },
    { fieldName: 'mergeToCustomerName', label: '合并至客户名', type: 'customerNameSelect' },
    { fieldName: 'mergeToCustomerCids', label: 'cid', type: 'multiTags' },
    {
      fieldName: 'status',
      label: '审核状态',
      render: () => {
        return (<Select placeholder="未筛选则默认全部" allowClear options={Object.entries(StatusMap).map(([key, value]) => ({ label: value, value: +key }))} />)
      },
    },

  ]

  const columns: TableColumnProps<CustomerMergeApprovalItem>[] = useMemo(() => [
    {
      title: '申请时间',
      dataIndex: 'createTime',
      width: 120,
      sorter: true,
      sortOrder: filter.sort === 'createTime' ? antdSortMap[filter.sortType] : undefined,
    },
    {
      title: '被合并客户名',
      dataIndex: 'mergedCustomer',
      width: 300,
      render: (mergedCustomer) => {
        const showMergedCustomer = mergedCustomer.slice(0, 3)
        const hasMore = mergedCustomer.length > 3
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            {showMergedCustomer.map(customer => (
              <Tooltip arrowPointAtCenter key={customer.customerId} title={customer.customerName} getPopupContainer={triggerNode => triggerNode.parentElement}>
                <span style={{ display: 'inline', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }} key={customer.customerId}>
                  <Link target="_blank" to={`/customer/${customer.customerId}?from=${encodeURIComponent(window.location.pathname)}`}>
                    {trimStr(customer.customerName, '-')}
                  </Link>
                </span>
              </Tooltip>
            ))}
            {hasMore
            && (
              <Popover trigger="click" content={
                <div>
                  {mergedCustomer.map(customer => (
                    <div key={customer.customerId}>
                      <Link target="_blank" to={`/customer/${customer.customerId}?from=${encodeURIComponent(window.location.pathname)}`}>
                        {trimStr(customer?.customerName, '-')}
                      </Link>
                    </div>
                  ))}
                </div>
              } getPopupContainer={triggerNode => triggerNode.parentElement}>
                <Button type="dashed" size="small">查看更多</Button>
              </Popover>
            )}
          </div>
        )
      },
    },
    {
      title: '合并至客户名',
      dataIndex: 'mergeToCustomer',
      width: 120,
      render: (mergeToCustomer) => mergeToCustomer ? (
        <Link target="_blank" to={`/customer/${mergeToCustomer?.customerId}?from=${encodeURIComponent(window.location.pathname)}`}>
          {trimStr(mergeToCustomer?.customerName, '-')}
        </Link>) : '-',
    },
    {
      title: 'CID',
      dataIndex: 'mergeToCustomer',
      width: 180,
      render: (mergeToCustomer) => (
        <span>{mergeToCustomer?.customerCid}</span>
      ),
    },
    { title: '申请人', dataIndex: 'operator', width: 180, render: value => <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><UserAvatar salesName={value} /></div> },
    { title: '审核状态', dataIndex: 'statusCh', width: 80 },
  ], [filter.sort, filter.sortType])
  const tableXWidth = useMemo(() => getTableXwidth(columns), [columns])

  /* 列表选择相关 */
  return (
    <>
      <Card className="mt20 mb20">
        <CommonFilters
          className="custom-Label"
          itemList={filterItems}
          onSubmit={filter => setFilter({ ...filter, page: 1 })}
          onReset={() => resetFilter(initFilter)}
        />
      </Card>
      <Card>
        <TableDownload className="ml20" exportMethod={exportCustomerMergeApprovalList} filter={filter} />
        <Table
          tableLayout="fixed"
          className="mt20"
          loading={loading}
          pagination={pagination}
          scroll={{ x: tableXWidth }}
          rowKey={rowKey}
          dataSource={list}
          columns={columns}
          onChange={onTableChange}
        />
      </Card>
    </>
  )
}
export default AssignExamine
